import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { GbGemeindeListEntry, GbgemeindeService } from '../../../core/gb-api';
import { PolitischeGemeindeListEntry, StammdatengemeindeService } from '../../../core/stammdaten-api';
import { Location, NgTemplateOutlet } from '@angular/common';
import { ComparatorService } from '../../../shared/services/comparator-service';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { ErweiterungConfigResponse } from '../../../core/base-api';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { gemeindeSearchModeName } from '../gemeinde.constants';
import { ListLoaderObservable } from '../../../shared/components/list-loader/list-loader-observable';
import { map } from 'rxjs/operators';
import {
  AbstractNavigateBackComponent
} from '../../../shared/components/abstract-component/abstract-navigate-back.component';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { SearchMode } from '../../../shared/enum/searchMode';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { TitleFunctionbarComponent } from '../../../shared/components/title-functionbar/title-functionbar.component';
import {
  FunctionbarSearchresultButtonsComponent
} from '../../../shared/components/title-functionbar/functionbar-searchresult-buttons/functionbar-searchresult-buttons.component';
import {
  ListLoaderWrapperComponent
} from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { CapSpinnerSmallComponent } from '../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import { LinkComponent } from '../../../shared/components/link/link.component';
import { MapsComponent } from '../../../shared/widgets/maps/maps.component';
import { TranslateNamePipe } from '../../../shared/pipes/simple/translate-name.pipe';
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

@Component({
  selector: 'cap-gemeinde-list',
  templateUrl: './gemeinde-list.component.html',
  styleUrls: ['./gemeinde-list.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    TitleFunctionbarComponent,
    FunctionbarSearchresultButtonsComponent,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    CapSpinnerSmallComponent,
    LinkComponent,
    MapsComponent,
    TranslatePipe,
    TranslateNamePipe
  ]
})
export class GemeindeListComponent extends AbstractNavigateBackComponent implements OnInit {
  listLoader: ListLoader<GbGemeindeListEntryVM>;
  hasPermissionPlan: boolean;
  isMapsLinkConfigured: boolean = false;

  constructor(
    private route: ActivatedRoute,
    protected translate: TranslateService,
    protected capAlertService: CapAlertService,
    protected location: Location,
    private stateService: SearchStateService,
    private gemeindeService: GbgemeindeService,
    private polGemeindeService: StammdatengemeindeService,
    protected comparatorService: ComparatorService,
    protected appFormatConfigService: FormatConfigService
  ) {
    super(translate, capAlertService, location);
    keysToTranslate<string>([
      'GB_GEMEINDEKREIS',
      'GB_GEMEINDE_OHNEKREIS',
      'SEYBUCH',
      'AUSLAENDISCHE_GEMEINDE',
      'gefundeneGemeinden',
      'Gemeinde.Kuerzel',
      'suchergebnis.gemeinden.PolitischeGemeinde',
      'detailsGemeindeGb'
    ]);
  }

  ngOnInit() {
    const searchMode: SearchMode = this.route.snapshot.params[gemeindeSearchModeName];
    const request = JSON.parse(this.route.snapshot.queryParams['request']);
    this.stateService.lastSearchMode = searchMode;
    this.createListLoader(request);
    this.hasPermissionPlan = this.isTableWithColumnPlan();
    this.appFormatConfigService.settingsConfig$.subscribe((config) => {
      this.isMapsLinkConfigured = config.isMapsLinkConfigured;
    });
  }

  private createListLoader(request: any) {
    const observable = this.gemeindeService
      .searchGbGemeindeStandard(request)
      .pipe(map((response) => this.createGemeindeViewModel(response.results)));
    this.listLoader = new ListLoaderObservable(15, 1, observable);
    this.addLoaderAndNavigateBackIfEmpty(this.listLoader);
  }

  private createGemeindeViewModel(gemeinden: GbGemeindeListEntry[]): GbGemeindeListEntryVM[] {
    const res = new Array<GbGemeindeListEntryVM>();
    gemeinden.forEach((g) => {
      res.push(new GbGemeindeListEntryVM(g, this.polGemeindeService, (sub: any): void => this.addSub(sub)));
    });
    return res;
  }

  isTableWithColumnPlan() {
    let erweiterungen: boolean;
    this.addSub(
      this.appFormatConfigService.erweiterungsConfig$.subscribe((config) => {
        erweiterungen = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.ANZEIGE_PLAN_GEM_KREIS);
      })
    );
    return erweiterungen;
  }
}

export class GbGemeindeListEntryVM {
  gbGemeinde: GbGemeindeListEntry;
  politischeGemeinde: PolitischeGemeindeListEntry;
  plan: boolean;

  constructor(
    public object: GbGemeindeListEntry,
    private polGemeindeService: StammdatengemeindeService,
    private addSub: any
  ) {
    this.gbGemeinde = object;
    this.plan = !(
      object.gemeindeArt === GbGemeindeListEntry.GemeindeArtEnum.SEYBUCH ||
      object.gemeindeArt === GbGemeindeListEntry.GemeindeArtEnum.AUSLAENDISCHE_GEMEINDE
    );
    if (this.object.gemeindeArt !== GbGemeindeListEntry.GemeindeArtEnum.AUSLAENDISCHE_GEMEINDE) {
      this.addSub(
        this.polGemeindeService.loadGemeinde(object.politischeGemeindeId.id).subscribe((g) => {
          this.politischeGemeinde = g;
          this.plan = this.plan && !g.extern;
        })
      );
    }
  }
}
